import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';

const FormInput = ({ label, name, value, onChange, type = "text", placeholder = "", required = false, readOnly = false }) => (
    <div>
        <label className="block font-semibold mb-1">{label}</label>
        <input
            type={type}
            name={name}
            value={value || ''} 
            onChange={onChange}
            placeholder={placeholder}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
            required={required}
            readOnly={readOnly}
        />
    </div>
);

const FormSection = ({ title, fields, formData, handleChange, line, readOnly = false }) => {
    // Define custom labels for measured fields
    const labelMap = {
        measuredPmax: 'Measured Pmax',
        measuredISC: 'Measured ISC',
        measuredVOC: 'Measured VOC',
        measuredIMP: 'Measured IMP',
        measuredVMP: 'Measured VMP',
        measuredFF: 'Measured FF',
        goldenModuleReport: 'Golden Module Report',
        moduleSerialNumber: 'Module Serial Number',
        alphaValue: 'Alpha Value',
        betaValue: 'Beta Value',
        gammaValue: 'Gamma Value',
        nominalPmax: 'Nominal Pmax',
        nominalISC: 'Nominal ISC',
        nominalVOC: 'Nominal VOC',
        nominalIMP: 'Nominal IMP',
        nominalVMP: 'Nominal VMP',
        nominalFF: 'Nominal FF',
    };

    return (
        <>
            <h3 className="text-xl font-semibold mt-4">{title}</h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-2">
                {fields.map((field) => (
                    <FormInput
                        key={field}
                        label={labelMap[field] || field.replace(/^[a-z]/, c => c.toUpperCase()).replace(/([A-Z])/g, ' $1')}
                        name={`${line}.${field}`}
                        value={formData[line]?.[field] || ''} 
                        onChange={handleChange}
                        type="text"
                        required={false}
                        readOnly={readOnly}
                    />
                ))}
            </div>
        </>
    );
};

const Form_11 = () => {
    const navigate = useNavigate();

    const fields = [
        { label: 'Golden Module Report', name: 'goldenModuleReport' },
        { label: 'Module Serial Number', name: 'moduleSerialNumber' },
        { label: 'Alpha Value', name: 'alphaValue', type: 'number' },
        { label: 'Beta Value', name: 'betaValue', type: 'number' },
        { label: 'Gamma Value', name: 'gammaValue', type: 'number' },
        { label: 'Nominal Pmax', name: 'nominalPmax', type: 'text' },
        { label: 'Nominal ISC', name: 'nominalISC', type: 'number' },
        { label: 'Nominal VOC', name: 'nominalVOC', type: 'number' },
        { label: 'Nominal IMP', name: 'nominalIMP', type: 'number' },
        { label: 'Nominal VMP', name: 'nominalVMP', type: 'number' },
        { label: 'Nominal FF', name: 'nominalFF', type: 'number' },
        { label: 'Measured Pmax', name: 'measuredPmax', type: 'number' },
        { label: 'Measured ISC', name: 'measuredISC', type: 'number' },
        { label: 'Measured VOC', name: 'measuredVOC', type: 'number' },
        { label: 'Measured IMP', name: 'measuredIMP', type: 'number' },
        { label: 'Measured VMP', name: 'measuredVMP', type: 'number' },
        { label: 'Measured FF', name: 'measuredFF', type: 'number' },
    ];

    const [lines, setLines] = useState([]);
    const [formData, setFormData] = useState({});
    const [date, setDate] = useState(() => sessionStorage.getItem('inspectionDate') || '');
    const [selectedLine, setSelectedLine] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Fetch lines from metadata and initialize form data
    useEffect(() => {
        const metadata = sessionStorage.getItem('metadata');
        if (metadata) {
            try {
                const parsedMetadata = JSON.parse(metadata);
                const fetchedLines = parsedMetadata.lines.map(item => String(item.Lines)); // Convert to string
                setLines(fetchedLines);
                setSelectedLine(fetchedLines[0] || '');

                const savedData = sessionStorage.getItem('PrimaryModuleCalibrationF11formData');
                let initialData = savedData ? JSON.parse(savedData) : null;

                // Retrieve calibration metadata
                const calibrationData = parsedMetadata?.primaryGoldenModuleCalibrationR || [];

                if (!initialData) {
                    initialData = fetchedLines.reduce((acc, line) => {
                        // Find matching calibration data for the current line
                        const calibrationEntry = calibrationData.find(entry => String(entry.Line) === String(line)) || {};
                        return {
                            ...acc,
                            [String(line)]: fields.reduce((fieldAcc, field) => ({
                                ...fieldAcc,
                                [field.name]: (
                                    field.name === 'alphaValue' ? calibrationEntry.Alpha :
                                    field.name === 'betaValue' ? calibrationEntry.Beta :
                                    field.name === 'gammaValue' ? calibrationEntry.Gamma :
                                    field.name === 'moduleSerialNumber' ? calibrationEntry.moduleSerialNumber :
                                    field.name === 'nominalPmax' ? calibrationEntry.nominalPmax :
                                    field.name === 'nominalISC' ? calibrationEntry.nominalISC :
                                    field.name === 'nominalVOC' ? calibrationEntry.nominalVOC :
                                    field.name === 'nominalIMP' ? calibrationEntry.nominalIMP :
                                    field.name === 'nominalVMP' ? calibrationEntry.nominalVMP :
                                    field.name === 'nominalFF' ? calibrationEntry.nominalFF :
                                    ''
                                ) || ''
                            }), {})
                        };
                    }, {});
                } else {
                    // Ensure all lines from metadata are present in formData and prefill from metadata if empty
                    fetchedLines.forEach(line => {
                        if (!initialData[line]) {
                            const calibrationEntry = calibrationData.find(entry => String(entry.Line) === String(line)) || {};
                            initialData[line] = fields.reduce((fieldAcc, field) => ({
                                ...fieldAcc,
                                [field.name]: (
                                    field.name === 'alphaValue' ? calibrationEntry.Alpha :
                                    field.name === 'betaValue' ? calibrationEntry.Beta :
                                    field.name === 'gammaValue' ? calibrationEntry.Gamma :
                                    field.name === 'moduleSerialNumber' ? calibrationEntry.moduleSerialNumber :
                                    field.name === 'nominalPmax' ? calibrationEntry.nominalPmax :
                                    field.name === 'nominalISC' ? calibrationEntry.nominalISC :
                                    field.name === 'nominalVOC' ? calibrationEntry.nominalVOC :
                                    field.name === 'nominalIMP' ? calibrationEntry.nominalIMP :
                                    field.name === 'nominalVMP' ? calibrationEntry.nominalVMP :
                                    field.name === 'nominalFF' ? calibrationEntry.nominalFF :
                                    ''
                                ) || ''
                            }), {});
                        } else {
                            // Update existing line data with metadata only if fields are empty
                            const calibrationEntry = calibrationData.find(entry => String(entry.Line) === String(line)) || {};
                            initialData[line] = fields.reduce((fieldAcc, field) => ({
                                ...fieldAcc,
                                [field.name]: initialData[line][field.name] || (
                                    field.name === 'alphaValue' ? calibrationEntry.Alpha :
                                    field.name === 'betaValue' ? calibrationEntry.Beta :
                                    field.name === 'gammaValue' ? calibrationEntry.Gamma :
                                    field.name === 'moduleSerialNumber' ? calibrationEntry.moduleSerialNumber :
                                    field.name === 'nominalPmax' ? calibrationEntry.nominalPmax :
                                    field.name === 'nominalISC' ? calibrationEntry.nominalISC :
                                    field.name === 'nominalVOC' ? calibrationEntry.nominalVOC :
                                    field.name === 'nominalIMP' ? calibrationEntry.nominalIMP :
                                    field.name === 'nominalVMP' ? calibrationEntry.nominalVMP :
                                    field.name === 'nominalFF' ? calibrationEntry.nominalFF :
                                    ''
                                ) || ''
                            }), {});
                        }
                    });
                }

                // Merge with inspectionData if formData is still empty
                const inspectionData = sessionStorage.getItem('inspectionData');
                if (inspectionData) {
                    try {
                        const parsedInspectionData = JSON.parse(inspectionData);
                        const calibrationRecord = parsedInspectionData.PrimaryModuleCalibrationRecord?.lines || {};
                        fetchedLines.forEach(line => {
                            if (calibrationRecord[line]) {
                                fields.forEach(field => {
                                    if (!initialData[line][field.name]) {
                                        initialData[line][field.name] = calibrationRecord[line][field.name] || initialData[line][field.name];
                                    }
                                });
                            }
                        });
                    } catch (error) {
                        console.error('Error parsing inspectionData:', error);
                    }
                }

                setFormData(initialData);
            } catch (error) {
                console.error('Error parsing metadata or saved data:', error);
            }
        }
    }, []);

    // Prefill date from sessionStorage
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        if (inspectionDate) setDate(inspectionDate);
    }, []);

    // Persist form data in sessionStorage
    useEffect(() => {
        if (Object.keys(formData).length > 0) {
            sessionStorage.setItem('PrimaryModuleCalibrationF11formData', JSON.stringify(formData));
        }
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const [line, field] = name.split('.');
        
        setFormData((prevData) => ({
            ...prevData,
            [String(line)]: { // Ensure line is a string
                ...prevData[line] || {},
                [field]: value
            }
        }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleLineChange = (e) => {
        setSelectedLine(String(e.target.value)); // Convert to string
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const hasData = lines.some(line => 
            Object.values(formData[line] || {}).some(value => value?.trim() !== '')
        );

        if (!hasData) {
            setErrors({ general: 'At least one field must contain data' });
            return;
        }

        setIsLoading(true);

        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: date,
                inspectionShift: sessionStorage.getItem('inspectionShift'),
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                PrimaryModuleCalibrationRecord: {
                    date,
                    lines: lines.reduce((acc, line) => ({
                        ...acc,
                        [String(line)]: formData[line] // Ensure line is a string
                    }), {})
                }
            };

            await submitFormData(payload);
            alert('Form submitted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const nominalFields = ['nominalPmax', 'nominalISC', 'nominalVOC', 'nominalIMP', 'nominalVMP', 'nominalFF'];
    const measuredFields = ['measuredPmax', 'measuredISC', 'measuredVOC', 'measuredIMP', 'measuredVMP', 'measuredFF'];

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
            <h2 className="text-2xl font-bold text-center mb-4">Primary (Golden) Module Calibration Record</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <FormInput 
                    label="Date" 
                    name="date" 
                    value={date} 
                    onChange={(e) => setDate(e.target.value)} 
                    type="date" 
                    required 
                    readOnly 
                />
                <div>
                    <label className="block font-semibold mb-1">Line</label>
                    <select
                        value={selectedLine}
                        onChange={handleLineChange} // Use new handler
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                        disabled={lines.length === 0}
                    >
                        {lines.length === 0 ? (
                            <option value="">No lines available</option>
                        ) : (
                            lines.map(line => (
                                <option key={line} value={line}>{line}</option>
                            ))
                        )}
                    </select>
                </div>
            </div>

            {lines.length > 0 && lines.map(line => (
                <div key={line} className={`space-y-4 ${selectedLine !== line ? 'hidden' : ''}`}>
                    <h3 className="text-xl font-semibold">{line}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput 
                            label="Golden Module Report" 
                            name={`${line}.goldenModuleReport`}
                            value={formData[line]?.goldenModuleReport || ''} 
                            onChange={handleChange} 
                        />
                        <FormInput 
                            label="Module Serial Number" 
                            name={`${line}.moduleSerialNumber`}
                            value={formData[line]?.moduleSerialNumber || ''} 
                            onChange={handleChange} 
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <FormInput 
                            label="Alpha Value" 
                            name={`${line}.alphaValue`}
                            value={formData[line]?.alphaValue || ''} 
                            onChange={handleChange} 
                            type="number" 
                            readOnly
                        />
                        <FormInput 
                            label="Beta Value" 
                            name={`${line}.betaValue`}
                            value={formData[line]?.betaValue || ''} 
                            onChange={handleChange} 
                            type="number" 
                            readOnly
                        />
                        <FormInput 
                            label="Gamma Value" 
                            name={`${line}.gammaValue`}
                            value={formData[line]?.gammaValue || ''} 
                            onChange={handleChange} 
                            type="number" 
                            readOnly
                        />
                    </div>

                    <FormSection 
                        title="Golden Module Nominal Values" 
                        fields={nominalFields} 
                        formData={formData} 
                        handleChange={handleChange}
                        line={line}
                        readOnly={true}
                    />
                    <FormSection 
                        title="Measured Values" 
                        fields={measuredFields} 
                        formData={formData} 
                        handleChange={handleChange}
                        line={line}
                    />
                </div>
            ))}

            {errors.general && <p className="text-red-500">{errors.general}</p>}

            <div className="flex justify-between mt-6">
                <button 
                    onClick={() => navigate('/inspections/section2/form10')} 
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                    Previous
                </button>
                <button 
                    type="submit" 
                    disabled={isLoading} 
                    className="px-4 py-2 bg-[#424752] text-white rounded hover:bg-gray-500 disabled:bg-gray-400"
                >
                    {isLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                    onClick={() => navigate('/inspections/section2/form12')} 
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default Form_11;